import { PDFCheckBox, PDFDocument, PDFDropdown, PDFRadioGroup, PDFTextField } from "pdf-lib";
import { buildReferenceSuggestions, collectFormSections, findReferenceSuggestion, getFieldLocation } from "./pdfAddendumLabels";
import { getFieldPageAndRect } from "./pdfFieldGeometry";

export const COPY_TO_CURRENT_VERSION_PERMISSION = "forms.actions.copy-to-current-version";

/** How each value is highlighted over the PDF of the old version, the one the user reviews. */
export const CopyHighlight = {
    Copied: "copied",
    CopiedWithWarning: "warning",
    NotCopied: "notCopied",
};

/** Why a value is not copied as it is, in the order they are shown. */
export const ReviewKind = {
    // The field does not exist in the new version: the value is not copied
    Missing: "missing",
    // The field exists but the value is not valid there: the value is not copied
    Invalid: "invalid",
    // The value is copied but it is longer than the field allows
    TooLong: "tooLong",
};

const HIGHLIGHT_BY_KIND = {
    [ReviewKind.Missing]: CopyHighlight.NotCopied,
    [ReviewKind.Invalid]: CopyHighlight.NotCopied,
    [ReviewKind.TooLong]: CopyHighlight.CopiedWithWarning,
};

const isBlank = (value) => value === null || value === undefined || String(value) === "";

// Unchecked checkboxes are stored as "false": they carry no data, so nothing is lost by not copying them
const isUnchecked = (value) => value === false || String(value).toLowerCase() === "false";

// Same rule PdfForm uses: the barcode is filled by the PDF itself and is not drawn over the page
const isBarcodeField = (field) =>
    field instanceof PDFTextField && field.acroField.getFullyQualifiedName().includes("PDF417BarCode");

/**
 * The version a client form can be copied to: the current, active version of the same form (issuer,
 * category and code), as long as it is newer than the given one.
 */
export const findCurrentVersion = (forms, form) => {
    if (!form || !forms) return undefined;
    return forms.find(
        (f) =>
            f.isCurrent &&
            f.status === "active" &&
            f.id > form.id &&
            f.issuer?.id === form.issuer?.id &&
            f.category?.id === form.category?.id &&
            String(f.code).toLowerCase() === String(form.code).toLowerCase()
    );
};

/** Fields and pages of the PDF, and the references an addendum can point to. */
export const loadPdfFormData = async (pdfUrl) => {
    const pdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    const fields = pdfDoc.getForm().getFields();
    const pages = pdfDoc.getPages();
    return {
        fields,
        pages,
        suggestions: buildReferenceSuggestions(collectFormSections(fields, pages)),
    };
};

/**
 * Where a field is, readable by a person: its page and its place in the form's outline, e.g.
 * { page: 3, label: "Part 2, Item 3.A: Family Name" }. Falls back to the field's internal name.
 */
export const describeField = (field, pages) => {
    const pageIndex = getFieldPageAndRect(field, pages)?.pageIndex;
    const { section, item, descriptor, partialName } = getFieldLocation(field);
    const place = [section, item.replace(/\.$/, "")].filter(Boolean).join(", ");
    const label = place && descriptor ? `${place}: ${descriptor}` : place || descriptor || partialName;
    return { page: pageIndex >= 0 ? pageIndex + 1 : null, label };
};

/** Checks a value against the field of the new version it would be copied to. */
const checkValue = (field, value) => {
    if (field instanceof PDFTextField) {
        if (isBarcodeField(field)) {
            return { copy: false, kind: ReviewKind.Invalid, reason: "Filled automatically by the PDF in the new version." };
        }
        const maxLength = field.getMaxLength();
        if (maxLength > 0 && String(value).length > maxLength) {
            return {
                copy: true,
                kind: ReviewKind.TooLong,
                reason: `Copied in full, but the field only shows the first ${maxLength} characters.`,
            };
        }
        return { copy: true };
    }
    if (field instanceof PDFCheckBox) {
        if (value === true || ["true", "false"].includes(String(value).toLowerCase())) return { copy: true };
        return { copy: false, kind: ReviewKind.Invalid, reason: "The field is a checkbox in the new version." };
    }
    if (field instanceof PDFRadioGroup || field instanceof PDFDropdown) {
        if (field.getOptions().includes(value)) return { copy: true };
        return {
            copy: false,
            kind: ReviewKind.Invalid,
            reason: `Not an option of the field in the new version. Options: ${field.getOptions().join(", ")}.`,
        };
    }
    return { copy: false, kind: ReviewKind.Invalid, reason: "The field cannot be filled in the new version." };
};

/**
 * Copies the values of a client form to another version of the form. A value is copied to the field whose
 * PDF name is exactly the same; a field that does not exist in the new version, or whose value is not valid
 * there, is reported instead.
 *
 * @param {{ sourceValues: Array, targetMappings: Array, target: Object, source: Object }} params
 *   sourceValues: the client form's values (with their formFieldMapping); targetMappings: the field
 *   mappings of the new version; target and source: { fields, pages } of the new and old versions' PDFs.
 * @returns {{ values: Object, highlights: Object, reviewItems: Array, copiedCount: number }}
 *   values: what is saved in the new version. highlights: how each value looks over the old version's PDF.
 *   Both keyed by PDF field name. reviewItems: { pdfField, value, kind, reason, copied, location }, the
 *   values not copied as they are, located in the old version.
 */
export const copyValuesToVersion = ({ sourceValues = [], targetMappings = [], target, source }) => {
    const mappedFieldNames = new Set(targetMappings.map((m) => m.pdfField));
    const targetFields = new Map(target.fields.map((f) => [f.getName(), f]));
    const sourceFields = new Map(source.fields.map((f) => [f.getName(), f]));
    const locate = (pdfField) => {
        const sourceField = sourceFields.get(pdfField);
        return sourceField ? describeField(sourceField, source.pages) : { page: null, label: pdfField };
    };
    const values = {};
    const highlights = {};
    const reviewItems = [];
    let copiedCount = 0;

    sourceValues.forEach(({ value, formFieldMapping }) => {
        const pdfField = formFieldMapping?.pdfField;
        if (!pdfField || isBlank(value)) return;

        const field = mappedFieldNames.has(pdfField) ? targetFields.get(pdfField) : undefined;
        const { copy, kind, reason } = field
            ? checkValue(field, value)
            : { copy: false, kind: ReviewKind.Missing, reason: "The field does not exist in the new version." };

        if (copy) values[pdfField] = value;
        // An unchecked checkbox is copied when it can be, but it is no data worth showing or counting
        if (isUnchecked(value)) return;

        if (copy) copiedCount += 1;
        if (kind) {
            highlights[pdfField] = HIGHLIGHT_BY_KIND[kind];
            reviewItems.push({ pdfField, value, kind, reason, copied: copy, location: locate(pdfField) });
        } else {
            highlights[pdfField] = CopyHighlight.Copied;
        }
    });

    // Reading order: by page, fields without a known page last
    reviewItems.sort((a, b) => (a.location.page || Number.MAX_SAFE_INTEGER) - (b.location.page || Number.MAX_SAFE_INTEGER));

    return { values, highlights, reviewItems, copiedCount };
};

/** Addendums copied as they are; only the page is looked up again, in the PDF of the new version. */
export const copyAddendums = (addendums = [], suggestions = []) =>
    addendums.map((addendum, index) => ({
        tempKey: `copied-${index}`,
        reference: addendum.reference || null,
        text: addendum.text,
        pageNumber: addendum.reference ? findReferenceSuggestion(addendum.reference, suggestions)?.pageNumber || null : null,
    }));
